import { FlightType, PensionType, TourismType, TravelClass } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { airportOptionSchema, companyPreferenceSchema, flightLegSchema, passengerSchema, } from './primitives.schema.js';
/**
 * `contactEmail` alimente directement `Client.email` (upsert par email dans
 * EstimateClientService). Sans `.email()` ici, une saisie type « nc » créait une
 * fiche client dont l'adresse était injectable telle quelle dans un envoi Brevo,
 * refusé en 400. Même règle que le formulaire (`contactEmailSchema` de
 * @massilia/features), désormais appliquée aussi côté API.
 */
const contactEmail = z.string().min(1, "L'adresse email est requise.").email('Email invalide.');
const baseEstimateSchema = z.object({
    clientFirstName: z.string().optional(),
    clientLastName: z.string().optional(),
    contactEmail,
    complementaryInformation: z.string(),
    contactPhoneNumber: z.string(),
    existingPassengerIds: z.array(z.string()).optional(),
    newPassengers: z.array(passengerSchema).optional(),
});
export const createFlightEstimateSchema = baseEstimateSchema.extend({
    companyPreference: companyPreferenceSchema,
    flights: z.array(flightLegSchema).min(1, 'At least one flight leg is required'),
    maxStayDuration: z.number().int().min(0).optional(),
    particularities: z.string().optional(),
    travelClass: z.nativeEnum(TravelClass),
    type: z.nativeEnum(FlightType),
});
export const createTourismEstimateSchema = baseEstimateSchema.extend({
    approximativeBudget: z.number().min(0),
    carLoan: z.boolean(),
    departure: airportOptionSchema,
    departureDate: z.string(),
    departureFlexibilityAfter: z.number().int().min(0),
    departureFlexibilityBefore: z.number().int().min(0),
    destination: airportOptionSchema,
    hotelMinimumNumberOfStars: z.number().int().min(0).nullable().optional(),
    maxStayDuration: z.number().int().min(0).optional(),
    onSiteActivities: z.boolean(),
    pension: z.nativeEnum(PensionType),
    returnDate: z.string(),
    returnFlexibilityAfter: z.number().int().min(0),
    returnFlexibilityBefore: z.number().int().min(0),
    transfertFromAirport: z.boolean(),
    type: z.nativeEnum(TourismType),
});
/**
 * Unified create-estimate contract. `estimateType` discriminates FLIGHT vs TOURISM,
 * but downstream helpers (buildFlightDtoFromUnified / buildTourismDtoFromUnified) read
 * every conditional field off a flat optional bag, so this stays a flat object with a
 * superRefine — not a discriminatedUnion, which would force narrowing at every call site.
 * Mirrors the legacy `@ValidateIf(o => o.estimateType === 'FLIGHT')` semantics: FLIGHT
 * estimates require companyPreference / travelClass / type; tourism fields stay optional.
 */
export const createEstimateSchema = z
    .object({
    // ---- Shared ----
    clientFirstName: z.string().optional(),
    clientLastName: z.string().optional(),
    contactEmail,
    // ---- Tourism-specific (optional on the unified shape) ----
    approximativeBudget: z.number().min(0).optional(),
    carLoan: z.boolean().optional(),
    // ---- Flight-specific (optional on the unified shape, required via superRefine) ----
    companyPreference: companyPreferenceSchema.optional(),
    complementaryInformation: z.string(),
    contactPhoneNumber: z.string().min(1),
    departure: airportOptionSchema.optional(),
    departureDate: z.string().optional(),
    departureFlexibilityAfter: z.number().int().min(0).optional(),
    departureFlexibilityBefore: z.number().int().min(0).optional(),
    destination: airportOptionSchema.optional(),
    estimateType: z.enum(['FLIGHT', 'TOURISM']),
    existingPassengerIds: z.array(z.string()).optional(),
    flights: z.array(flightLegSchema).min(1, 'At least one flight leg is required').optional(),
    hotelMinimumNumberOfStars: z.number().nullable().optional(),
    maxStayDuration: z.number().int().min(0).optional(),
    newPassengers: z.array(passengerSchema).optional(),
    onSiteActivities: z.boolean().optional(),
    particularities: z.string().optional(),
    pension: z.nativeEnum(PensionType).optional(),
    returnDate: z.string().optional(),
    returnFlexibilityAfter: z.number().int().min(0).optional(),
    returnFlexibilityBefore: z.number().int().min(0).optional(),
    transfertFromAirport: z.boolean().optional(),
    travelClass: z.nativeEnum(TravelClass).optional(),
    type: z.union([z.nativeEnum(FlightType), z.nativeEnum(TourismType)]).optional(),
})
    .superRefine((data, ctx) => {
    if (data.estimateType === 'FLIGHT') {
        if (!data.companyPreference) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'companyPreference is required for FLIGHT estimates',
                path: ['companyPreference'],
            });
        }
        if (!data.travelClass) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'travelClass is required for FLIGHT estimates',
                path: ['travelClass'],
            });
        }
        if (!data.type) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'type is required for FLIGHT estimates',
                path: ['type'],
            });
        }
    }
    if (data.estimateType === 'TOURISM') {
        // departure & destination are hard-required downstream (buildTourismDtoFromUnified
        // throws when either is missing) — validate here so clients get a 400 instead of a
        // 500. The other tourism fields stay optional: the build helpers apply defaults,
        // matching the original class-validator DTO where they were @IsOptional.
        if (!data.departure) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'departure is required for TOURISM estimates',
                path: ['departure'],
            });
        }
        if (!data.destination) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'destination is required for TOURISM estimates',
                path: ['destination'],
            });
        }
    }
});
